import { useInView } from "framer-motion";
import React, { FC, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { useDataLayer } from "../../../hooks/use-data-layer";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Button } from "../../buttons/button";
import { FormFieldColors, Select, TextInput } from "../../forms";
import { Skeleton } from "../../skeleton";
import { JobsFiltersForm } from "../../strips/jobs-grid-section/use-jobs-filters-form";
import { JobData } from "../types";
import { getJobTitle } from "../utils";
import { JobCard } from "./job-card";
import { JobsGridLayout } from "./jobs-grid-layout";

export interface JobsGridProps extends Queries.JobsGridSectionDataFragment {
  labels: Record<string, string | null>;
  filteredJobs: JobData[] | null;
  allJobs: JobData[] | null;
  page?: number;
  totalPages?: number;
  loading?: boolean;
  error: string | null;
  getLinkToJobDetailPage: (id: string) => string;
  onFilter: () => void;
}

export const JobsGrid: FC<JobsGridProps> = ({
  allJobs,
  filteredJobs,
  loading,
  getLinkToJobDetailPage,
  labels,
  error,
  onFilter,
}) => {
  const jobsCountParam = "{{count}}";
  const jobsCount = (filteredJobs?.length ?? 0).toString();
  const jobsCountLabel = labels["jobs-available-count"];
  const jobsCountString = jobsCountLabel?.includes(jobsCountParam)
    ? jobsCountLabel?.replace(jobsCountParam, jobsCount)
    : `${jobsCount} ${jobsCountLabel}`;

  const { setValue, reset, watch } = useFormContext<JobsFiltersForm>();
  const filters = watch();

  const [positionsOptions, locationsOptions, macrofunctionsOptions] = useMemo(() => {
    const positions = new Set<string>();
    const locations = new Set<string>();
    const macrofunctions = new Set<string>();

    allJobs?.forEach((job) => {
      positions.add(getJobTitle(job));
      locations.add(job.country);
      if (job.macrofunction) {
        macrofunctions.add(job.macrofunction);
      }
    });

    return [positions, locations, macrofunctions].map((s) => Array.from(s));
  }, [allJobs]);

  const handleReset = () => {
    reset({
      position: "",
      location: "",
      macrofunction: "",
    });
    onFilter();
  };

  const { location, macrofunction, position } = filters;

  const skeletons = ["", "hidden sm:block", "hidden lg:block", "hidden 2xl:block"];

  const isFormDisabled = loading;

  const renderOptions = (
    options: string[],
    getLabel: (value: string) => string = (value) => value
  ) =>
    options.map((value) => (
      <option key={value} value={value}>
        {getLabel(value)}
      </option>
    ));

  // Send an event the first time the job grid is viewed (and only first time)
  const { pushEvent } = useDataLayer();
  const gridRef = useRef<HTMLDivElement>(null);
  const isJobsGridVisible = useInView(gridRef);
  const hasSentViewEvent = useRef(false);
  useEffect(() => {
    if (isJobsGridVisible && !hasSentViewEvent.current) {
      pushEvent({
        event: GoogleAnalyticsEvent.ViewJobList,
      });
      hasSentViewEvent.current = true;
    }
  }, [isJobsGridVisible, pushEvent]);

  return (
    <JobsGridLayout
      ref={gridRef}
      labels={labels}
      error={error}
      positionsField={
        <TextInput
          className="w-full"
          color={FormFieldColors.Light}
          disabled={isFormDisabled}
          placeholder={labels["jobs-all-positions"] ?? ""}
          type="search"
          value={position}
          onChangeValue={(v) => setValue("position", v)}
          transformOptions={(text: string) =>
            positionsOptions
              .filter((value) => value.toLowerCase().includes(text.toLowerCase()))
              .slice(0, 5)
              .map((value) => ({ value, label: value }))
          }
        />
      }
      locationsField={
        <Select
          disabled={isFormDisabled}
          value={location}
          onChange={(e) => setValue("location", e.target.value)}
        >
          <option value="">{labels["jobs-all-locations"]}</option>
          {renderOptions(locationsOptions)}
        </Select>
      }
      macrofunctionsField={
        macrofunctionsOptions.length > 0 && (
          <Select
            disabled={isFormDisabled}
            value={macrofunction}
            onChange={(e) => setValue("macrofunction", e.target.value)}
          >
            <option value="">{labels["jobs-all-macrofunctions"]}</option>
            {renderOptions(macrofunctionsOptions)}
          </Select>
        )
      }
      formCancelButton={
        <Button variant="primary-outline" size="sm" onClick={handleReset} disabled={isFormDisabled}>
          {labels["jobs-filter-clear"]}
        </Button>
      }
      formSubmitButton={
        <Button variant="primary" size="sm" onClick={onFilter} disabled={isFormDisabled}>
          {labels["jobs-filter-submit"]}
        </Button>
      }
      jobsCount={loading ? <Skeleton width={256} /> : jobsCountString}
      jobs={
        loading ? (
          skeletons.map((classes, i) => <Skeleton height={320} key={i} className={classes} />)
        ) : error ? (
          <div className="text-center mx-auto">{error}</div>
        ) : filteredJobs ? (
          filteredJobs.map((job) => (
            <JobCard
              key={job.jobId}
              job={job}
              detailLink={getLinkToJobDetailPage(job.jobId)}
              labels={labels}
            />
          ))
        ) : null
      }
    />
  );
};
